<template>
  <div :class="[bemBlock, 'image-container']">
    <a
      draggable="false"
      :class="bemElement('link')"
      :href="url"
      @click="handleLinkClick"
    >
      <BaseCarousel
        v-if="shouldShowCarousel"
        bem="base-product-tile-carousel"
        :are-controls-shown="areCarouselControlsShown"
        :items="productImageSets"
        :context-product-id="productId"
        :swiper-options="{ allowTouchMove: !isSliderContext }"
      >
        <template #default="slotProps">
          <BaseImage
            :class="slotProps.classname"
            :image-classes="bemElement('image')"
            :image="getDefaultImageFromProductImageSet(slotProps.item)"
            :loading="
              forceMainImageToLoad && slotProps.index === 0
                ? 'eager'
                : slotProps.loading
            "
            :placeholder="
              getPlaceholderImageFromProductImageSet(slotProps.item)
            "
            :sizes="sizes"
            :use-title="false"
            :srcset="getSrcsetFromProductImageSet(slotProps.item)"
            @load="handleImageLoad(slotProps.index)"
          />
        </template>
      </BaseCarousel>
      <BaseImage
        v-else
        :class="bemElement('pic')"
        :image-classes="bemElement('image')"
        :image="getDefaultImageFromProductImageSet(mainImage)"
        :loading="forceMainImageToLoad ? 'eager' : null"
        :placeholder="getPlaceholderImageFromProductImageSet(mainImage)"
        :sizes="sizes"
        :srcset="getSrcsetFromProductImageSet(mainImage)"
        :use-title="false"
        @load="handleImageLoad(0)"
      />
    </a>
  </div>
</template>

<script>
import bemClassesMixin from '../mixins/bemClassesMixin';
import productPropBaseMixin from '../mixins/productPropBaseMixin';
import { productImageSetMixin } from '../mixins/productImageSetMixin';
import BaseCarousel from './BaseCarousel.vue';
import BaseImage from './BaseImage.vue';
import getFrontConfig from '../../common/getFrontConfig';
import windowResizeMixin from '../mixins/windowResizeMixin';

/*
img sources
min-width:768px => 25w
50w

srcset:
200px,
400px,
600px,
800px,
1200px,

Resolutions - Tile img size
375 x ...: 186x278 (1x), 372x556 (2x)
768 x ...: 190x285 (1x), 380x570 (2x)
1024 x ...: 254x381 (1x), 508x762 (2x)
1440 x 900: 328x493 (1x), 656x986 (2x)
2560 x 1080: 586x879
2560 x 1600 (Macbook retina 13"): 1172x1758 (2x)
5120 x 2880: 1174x1761 (1x), 2348x3522 (2x)
*/
const SIZES = {
  MINICART_SLIDER:
    '(min-width: 1400px) 7vw, (min-width: 1025px) 10vw, (min-width: 768px) 19vw, 30vw',
  PDP_COMPLETE_THE_LOOK:
    '(min-width: 1025px) 16.6vw, (min-width: 768px) 28vw, 40vw',
  PLP_SLIDER: '40vw',
  PLP_GRID: '(min-width: 768px) 25vw, 50vw',
  PLP_GRID_FULL_WIDTH: '(min-width: 768px) 25vw, 100vw',
  SEARCH_MODAL:
    '(min-width: 1400px) 7vw, (min-width: 1025px) 10vw, (min-width: 768px) 19vw, 30vw',
};

const {
  productImages: { imageSets },
  responsive: { bp_768: bp768 },
  vue: {
    BaseProductTile: { context },
  },
} = getFrontConfig();

export default {
  name: 'BaseProductTileImage',

  components: {
    BaseCarousel,
    BaseImage,
  },

  mixins: [
    bemClassesMixin,
    productPropBaseMixin,
    productImageSetMixin,
    windowResizeMixin,
  ],

  props: {
    hasCarousel: {
      type: Boolean,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    context: {
      type: String,
      required: false,
      default: context.plpGrid,
    },
    forceMainImageToLoad: {
      default: false,
      required: false,
      type: Boolean,
    },
    areCarouselControlsShown: {
      default: true,
      type: Boolean,
    },
  },

  data: () => ({
    dataChangeBreakpoint: bp768,
  }),

  computed: {
    shouldShowCarousel() {
      return (this.hasCarousel && this.productImageSets?.length > 1) ?? false;
    },
    isSliderContext() {
      return (
        this.context === context.minicartSlider ||
        this.context === context.pdpSlider ||
        this.context === context.plpSliderBanner ||
        this.context === context.pdpRecommendedSlider ||
        this.context === context.searchModal
      );
    },
    mainImage() {
      return this.context === context.plpSliderBanner && this.product.image
        ? this.createCustomImageObject(this.product)
        : this.productImageSets?.[0];
    },
    productImageSets() {
      let imageSet = imageSets.PLP;
      let fallbackImageSet = null;
      if (
        this.context === context.plpGridFullWidth &&
        this.isBreakpointMobile
      ) {
        imageSet = imageSets.PLP_FULL_WIDTH;
        fallbackImageSet = imageSets.PLP;
      } else if (this.isSliderContext) {
        imageSet = imageSets.PLAIN;
      }
      let result = this.getProductImageSets(imageSet);
      if (result.length === 0 && fallbackImageSet) {
        result = this.getProductImageSets(fallbackImageSet);
      }
      return result;
    },
    sizes() {
      let result = SIZES.PLP_GRID;
      if (this.context === context.plpGridFullWidth) {
        result = SIZES.PLP_GRID_FULL_WIDTH;
      } else if (this.context === context.pdpSlider) {
        result = SIZES.PDP_COMPLETE_THE_LOOK;
      } else if (this.context === context.minicartSlider) {
        result = SIZES.MINICART_SLIDER;
      } else if (this.context === context.plpSliderBanner) {
        result = SIZES.PLP_SLIDER;
      } else if (this.context === context.searchModal) {
        result = SIZES.SEARCH_MODAL;
      }
      return result;
    },
  },

  methods: {
    handleImageLoad(index) {
      if (+index === 0) {
        this.$emit('load-main-image');
      }
    },
    handleLinkClick($event) {
      if (!$event.ctrlKey) {
        $event.preventDefault();
        const isCarouselControlClick = !!$event.target.closest(
          '.base-product-tile-carousel__control'
        );
        if (isCarouselControlClick) {
          // if the click is on a control/arrow of the carousel, abort the click
          return;
        }
        this.$emit('click', $event);
      }
    },
  },
};
</script>
